/* =============================================
   Subcollection Panel
   File: assets/subcollection-panel.js

   Desktop (>989px):
   - On init, ALL panels are moved to <body> permanently
     and given position:fixed. They never move back.
   - Hover card → position and show the panel
   - Hover list item → swap image + description

   Mobile/Tablet (≤989px):
   - Panels hidden via CSS
   - Tap card title → inline accordion expands
   ============================================= */

(function () {

  const MOBILE_BP = 989;

  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  /* One active panel at a time */
  let activePanel   = null;
  let activeWrapper = null;
  let hideTimer     = null;

  /* ── Position panel using card's viewport coords ── */
  function positionPanel(panel, wrapper) {
    const cardRect = wrapper.getBoundingClientRect();
    const panelW   = panel.offsetWidth  || 945;
    const panelH   = 250;
    const vpW      = window.innerWidth;

    let left = cardRect.left;
    let top  = cardRect.top - panelH - 4;

    /* Clamp horizontally */
    if (left + panelW > vpW - 16) left = vpW - panelW - 200;
    if (left < 16) left = -200;

    /* Not enough space above → show below */
    if (top < 16) top = cardRect.top + 200;

    panel.style.left = left + 'px';
    panel.style.top  = top  + 'px';
  }

  /* ── Show ── */
  function showPanel(panel, wrapper) {
    if (isMobile()) return;

    clearTimeout(hideTimer);

    /* Hide any other open panel instantly */
    if (activePanel && activePanel !== panel) {
      activePanel.classList.remove('is-showing');
    }

    /* Position then show */
    positionPanel(panel, wrapper);
    panel.classList.add('is-showing');

    activePanel   = panel;
    activeWrapper = wrapper;
  }

  /* ── Hide ── */
  function scheduleHide(panel) {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (!panel) return;
      /* Double check neither card nor panel is hovered */
      if (panel.matches(':hover')) return;
      if (activeWrapper && activeWrapper.matches(':hover')) return;
      panel.classList.remove('is-showing');
      if (activePanel === panel) {
        activePanel   = null;
        activeWrapper = null;
      }
    }, 80);
  }

  /* ══════════════════════════════════════════
     DESKTOP
  ══════════════════════════════════════════ */

  function initDesktopPanel(wrapper) {
    const panel      = wrapper.querySelector('.sc-panel');
    const listItems  = wrapper.querySelectorAll('.sc-panel__list-item');
    const imageWraps = wrapper.querySelectorAll('.sc-panel__image-wrap');
    const details    = wrapper.querySelectorAll('.sc-panel__detail');

    if (!panel) return;

    /* ── Permanently teleport panel to body ──
       Done once at init. Panel stays on body forever.
       No more moving it back and forth.              */
    document.body.appendChild(panel);

    function activateIndex(index) {
      listItems.forEach((item, i)   => item.classList.toggle('is-active',  i === index));
      imageWraps.forEach((img, i)   => img.classList.toggle('is-active',   i === index));
      details.forEach((detail, i)   => detail.classList.toggle('is-active', i === index));
    }

    /* Activate first item by default */
    if (listItems.length) activateIndex(0);

    /* Card hover */
    wrapper.addEventListener('mouseenter', () => showPanel(panel, wrapper));
    wrapper.addEventListener('mouseleave', () => scheduleHide(panel));

    /* Panel hover — cancel any pending hide */
    panel.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    panel.addEventListener('mouseleave', () => scheduleHide(panel));

    /* List item hover → swap content */
    listItems.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (isMobile()) return;
        activateIndex(parseInt(item.dataset.subIndex, 10));
      });
    });

    /* Reposition on scroll/resize */
    window.addEventListener('scroll', () => {
      if (activePanel === panel) positionPanel(panel, wrapper);
    }, { passive: true });

    window.addEventListener('resize', () => {
      if (activePanel === panel) positionPanel(panel, wrapper);
    }, { passive: true });
  }

  /* ══════════════════════════════════════════
     MOBILE ACCORDION
  ══════════════════════════════════════════ */

  let openAccordion = null;

  function closeAccordion(wrapper) {
    const accordion = wrapper.querySelector('.sc-accordion');
    const trigger   = wrapper.querySelector('.sc-accordion__trigger');
    if (!accordion) return;

    accordion.style.maxHeight  = '0px';
    accordion.style.visibility = 'hidden';
    accordion.setAttribute('aria-hidden', 'true');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    wrapper.classList.remove('sc-accordion--open');
    openAccordion = null;
  }

  function openAccordionFn(wrapper) {
    const accordion = wrapper.querySelector('.sc-accordion');
    const trigger   = wrapper.querySelector('.sc-accordion__trigger');
    if (!accordion) return;

    if (openAccordion && openAccordion !== wrapper) {
      closeAccordion(openAccordion);
    }

    accordion.style.visibility = 'visible';
    accordion.style.maxHeight  = accordion.scrollHeight + 'px';
    accordion.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    wrapper.classList.add('sc-accordion--open');
    openAccordion = wrapper;
  }

  function initMobileAccordion(wrapper) {
    const accordion = wrapper.querySelector('.sc-accordion');
    const trigger   = wrapper.querySelector('.sc-accordion__trigger');
    if (!accordion || !trigger) return;

    accordion.style.maxHeight  = '0px';
    accordion.style.visibility = 'hidden';
    accordion.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('click', () => {
      if (wrapper.classList.contains('sc-accordion--open')) {
        closeAccordion(wrapper);
      } else {
        openAccordionFn(wrapper);
      }
    });
  }

  /* ══════════════════════════════════════════
     INIT
  ══════════════════════════════════════════ */

  function initWrapper(wrapper) {
    /* Clear any animation-related stacking context leftovers */
    const animatedParent = wrapper.closest('.collection-list__item') || wrapper;
    animatedParent.style.willChange = 'auto';
    animatedParent.style.transform  = '';

    initDesktopPanel(wrapper);
    initMobileAccordion(wrapper);
  }

  function init() {
    const wrappers = Array.from(
      document.querySelectorAll('.card-wrapper.has-subcollections')
    );

    wrappers.forEach((wrapper) => {
      const animatedParent = wrapper.closest('.scroll-trigger, [data-cascade]')
                          || wrapper.closest('.collection-list__item')
                          || wrapper;

      const style      = getComputedStyle(animatedParent);
      const hasAnim    = style.animationName && style.animationName !== 'none';
      const hasTrans   = style.transitionProperty && style.transitionProperty !== 'none';

      if (!hasAnim && !hasTrans) {
        initWrapper(wrapper);
        return;
      }

      let settled = false;

      function onSettled() {
        if (settled) return;
        settled = true;
        animatedParent.removeEventListener('animationend',  onSettled);
        animatedParent.removeEventListener('transitionend', onSettled);
        initWrapper(wrapper);
      }

      animatedParent.addEventListener('animationend',  onSettled);
      animatedParent.addEventListener('transitionend', onSettled);

      /* Fallback in case events never fire */
      setTimeout(() => onSettled(), 2500);
    });

    window.addEventListener('resize', () => {
      if (!isMobile() && openAccordion) closeAccordion(openAccordion);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();